import axios from "axios";
import { useEffect, useRef } from "react";
import { getCurrentChainId } from "../../../config/chains";
import { ChainIdEnum } from "../../../enums/chain";
import { API_URL } from "../../../utils/env";
import useWallet from "../../wallet/useWallet";

const AUTOMATIC_FAUCET_CHAINS = new Set<number>([
  ChainIdEnum.MONAD_TESTNET,
  ChainIdEnum.ARC_TESTNET,
  ChainIdEnum.DOGEOS_TESTNET,
]);

const MAX_TRANSIENT_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [500, 1500];
const ATTEMPT_KEY_PREFIX = "marginx:auto-faucet";

// If the first funding check is still pending after this delay we surface the
// "funds are on the way" onboarding hint. A wallet that already holds enough
// collateral resolves as `sufficient` well before this fires, so funded users
// never see the message.
const FUNDING_HINT_DELAY_MS = 1200;

export interface AutoFaucetTransaction {
  token: string;
  amount: string;
  txHash: string;
}

export type AutoFaucetPhase = "funding" | "funded" | "sufficient" | "error";

export interface AutoFaucetStatusEvent {
  phase: AutoFaucetPhase;
  address: string;
  chainId: number;
  transactions?: AutoFaucetTransaction[];
  quoteBalance?: string;
}

// Module-level pub/sub decouples the singleton funding request (which is
// deduplicated across StrictMode double-mounts) from the React tree that renders
// the onboarding toast + dialog. The request emits lifecycle phases; a single
// notifier component subscribes and drives the UI.
const statusListeners = new Set<(event: AutoFaucetStatusEvent) => void>();

const emitStatus = (event: AutoFaucetStatusEvent) => {
  statusListeners.forEach((listener) => {
    try {
      listener(event);
    } catch {
      // A listener must never break the funding flow.
    }
  });
};

/**
 * Subscribe to automatic-faucet lifecycle events. Returns an unsubscribe fn.
 * Funding is best-effort onboarding UX and must never block wallet login, so
 * listeners should treat `error` as advisory only.
 */
export const subscribeAutomaticFaucet = (
  listener: (event: AutoFaucetStatusEvent) => void
): (() => void) => {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
};

// React StrictMode may mount effects twice. An in-memory guard keeps a continuous
// login deduplicated while allowing a page reload to recheck on-chain eligibility.
const attemptedRequests = new Set<string>();
const activeRequestSessions = new Map<string, { cancelled: boolean }>();
const attemptConsumers = new Map<string, Set<symbol>>();

const getAttemptKey = (address: string, chainId: number) =>
  `${ATTEMPT_KEY_PREFIX}:${chainId}:${address.toLowerCase()}`;

const wasAttempted = (key: string): boolean => attemptedRequests.has(key);

const markAttempted = (key: string) => {
  attemptedRequests.add(key);
};

const clearAttempt = (key: string) => {
  attemptedRequests.delete(key);
};

const cancelAttemptSession = (key: string) => {
  clearAttempt(key);
  const session = activeRequestSessions.get(key);
  if (session) session.cancelled = true;
  activeRequestSessions.delete(key);
  attemptConsumers.delete(key);
};

const registerAttemptConsumer = (key: string) => {
  const consumer = Symbol(key);
  const consumers = attemptConsumers.get(key) || new Set<symbol>();
  consumers.add(consumer);
  attemptConsumers.set(key, consumers);

  return () => {
    // React StrictMode tears down and recreates effects synchronously. Deferring
    // cleanup lets the replacement effect adopt the existing request session.
    window.setTimeout(() => {
      if (attemptConsumers.get(key) !== consumers) return;
      consumers.delete(consumer);
      if (consumers.size === 0) cancelAttemptSession(key);
    }, 0);
  };
};

const wait = (delayMs: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, delayMs));

const isRetryableError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return status === undefined || status === 429 || status >= 500;
};

const retryDelayMs = (error: unknown, attempt: number): number => {
  if (axios.isAxiosError(error) && error.response?.status === 429) {
    const rawRetryAfter = error.response.headers?.["retry-after"];
    if (typeof rawRetryAfter === "string") {
      const seconds = Number(rawRetryAfter);
      if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;

      const retryAt = Date.parse(rawRetryAfter);
      if (Number.isFinite(retryAt)) return Math.max(0, retryAt - Date.now());
    }
  }
  return RETRY_DELAYS_MS[attempt];
};

const requestAutomaticFaucet = async (
  address: string,
  chainId: number,
  isCurrentAttempt: () => boolean
) => {
  let transientAttempt = 0;
  let fundingHintEmitted = false;

  const emitFundingHint = () => {
    if (fundingHintEmitted || !isCurrentAttempt()) return;
    fundingHintEmitted = true;
    emitStatus({ phase: "funding", address, chainId });
  };

  // The first request confirms the transfer on-chain before it resolves, so it
  // can take several seconds. Surface the "funds are on the way" hint if it is
  // still pending after a short grace period rather than waiting silently.
  const hintTimer = window.setTimeout(emitFundingHint, FUNDING_HINT_DELAY_MS);

  try {
    while (isCurrentAttempt()) {
      try {
        const response = await axios.post(`${API_URL}/faucet/auto`, {
          address,
          chainId,
        });
        if (!isCurrentAttempt()) return;
        transientAttempt = 0;
        const data = response.data ?? {};
        if (data.reason === "in_progress") {
          // Another request is already funding this address — confirm the hint.
          emitFundingHint();
          const retryAfterSeconds = Number(data.retryAfterSeconds);
          await wait(
            Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
              ? retryAfterSeconds * 1000
              : RETRY_DELAYS_MS[0]
          );
          continue;
        }
        if (data.funded === true || data.reason === "funded") {
          emitStatus({
            phase: "funded",
            address,
            chainId,
            transactions: Array.isArray(data.transactions)
              ? data.transactions
              : undefined,
          });
        } else {
          // `sufficient` or `cooldown`: the wallet is already funded. Report it
          // so the notifier can dismiss any funding hint without a success UI.
          emitStatus({
            phase: "sufficient",
            address,
            chainId,
            quoteBalance:
              typeof data.quoteBalance === "string"
                ? data.quoteBalance
                : undefined,
          });
        }
        return;
      } catch (error) {
        if (!isCurrentAttempt()) return;
        const isLastAttempt = transientAttempt === MAX_TRANSIENT_ATTEMPTS - 1;
        if (isLastAttempt || !isRetryableError(error)) {
          throw error;
        }
        await wait(retryDelayMs(error, transientAttempt));
        transientAttempt += 1;
      }
    }
  } finally {
    window.clearTimeout(hintTimer);
  }
};

const startAutomaticFaucetAttempt = (
  key: string,
  address: string,
  chainId: number
) => {
  if (wasAttempted(key) || activeRequestSessions.has(key)) return;

  markAttempted(key);
  const session = { cancelled: false };
  activeRequestSessions.set(key, session);
  void requestAutomaticFaucet(
    address,
    chainId,
    () => activeRequestSessions.get(key) === session && !session.cancelled
  )
    .catch((error) => {
      // Permit a later genuine reconnect after the bounded transient retries fail.
      // Client errors are terminal for this continuous login and stay deduplicated.
      if (isRetryableError(error)) clearAttempt(key);
      // Funding is best-effort and must never block or fail wallet login. Report
      // the failure so the notifier can clear a dangling funding hint; it is
      // advisory only and shows no blocking error UI.
      if (!session.cancelled) {
        emitStatus({ phase: "error", address, chainId });
      }
    })
    .finally(() => {
      if (activeRequestSessions.get(key) === session) {
        activeRequestSessions.delete(key);
      }
    });
};

/**
 * Trigger the server-side testnet funding check after a wallet session becomes
 * active. Eligibility and transfers are intentionally decided by the backend.
 */
export const useAutomaticFaucet = () => {
  const { account, active, chainId } = useWallet();
  const activeAttemptKeyRef = useRef<string>();

  useEffect(() => {
    const clearPreviousAttempt = () => {
      if (!activeAttemptKeyRef.current) return;
      cancelAttemptSession(activeAttemptKeyRef.current);
      activeAttemptKeyRef.current = undefined;
    };

    if (!active || !account || chainId === undefined) {
      clearPreviousAttempt();
      return;
    }

    const actualChainId = Number(chainId);
    if (
      actualChainId !== getCurrentChainId() ||
      !AUTOMATIC_FAUCET_CHAINS.has(actualChainId)
    ) {
      clearPreviousAttempt();
      return;
    }

    const key = getAttemptKey(account, actualChainId);
    if (
      activeAttemptKeyRef.current &&
      activeAttemptKeyRef.current !== key
    ) {
      clearPreviousAttempt();
    }
    activeAttemptKeyRef.current = key;
    const unregisterConsumer = registerAttemptConsumer(key);
    startAutomaticFaucetAttempt(key, account, actualChainId);
    return unregisterConsumer;
  }, [account, active, chainId]);
};
